// src/components/HeaderComponents.jsx
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/header.css';

export default function Header({ isScrolled, isMenuOpen, toggleMenu, data, smoothScrollTo }) {
  const location = useLocation();

  // ✅ 모든 Hook은 컴포넌트 최상단에서 호출
  const [openSet, setOpenSet] = useState(() => new Set());
  const isMobile = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 968px)').matches;
  const isHome = location.pathname === '/' || location.pathname === '';

  const labelDropdowns = useMemo(
    () => ({
      '다이빙이지': [
        { label: '회사 소개', href: '/about' },
        { label: '강사 소개', href: '/instructors' },
        { label: '파트너사', href: '/partners' },
        { label: '협력센터', href: '/centers' },
      ],
      '강습 과정': [
        { label: '강습 과정', href: '/courses' },
        { label: '강습 장소', href: '/places' },
      ],
      '강습과정': [
        { label: '강습 과정', href: '/courses' },
        { label: '강습 장소', href: '/places' },
      ],
      '고객 센터': [
        { label: '공지사항', href: '/support/notice' },
        { label: '자주묻는 질문', href: '/support/faq' },
      ],
      '고객센터': [
        { label: '공지사항', href: '/support/notice' },
        { label: '자주묻는 질문', href: '/support/faq' },
      ],
    }),
    []
  );

  // ✅ Hook 호출 뒤에 early return 배치
  if (!data) return null;

  const getChildren = (m) => (m.children?.length ? m.children : labelDropdowns[m.label]) || null;

  const toggleSection = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const onAnchorClick = (e) => {
    const href = e.currentTarget.getAttribute('href');
    if (href?.startsWith('#')) {
      e.preventDefault();
      smoothScrollTo?.(href);
      if (isMenuOpen) toggleMenu();
    }
  };

  // 로고 이미지 선택: 홈페이지이고 스크롤 전이면 흰색, 아니면 검은색
  const logoSrc = (isHome && !isScrolled) 
    ? data.site?.logoLight 
    : data.site?.logoDark;

  return (
    <header
      id="header"
      className={`header ${isScrolled ? 'scrolled' : ''} ${isHome ? 'home-header' : 'sub-header'}`}
    >
      <div className="nav-container">
        <a href="/" className="logo">
          {logoSrc ? (
            <img src={logoSrc} alt={data.site?.brand || 'DIVINGEASY'} className="logo-img" />
          ) : (
            data.site?.brand || 'DIVINGEASY'
          )}
        </a>

        <nav>
          <ul className={`nav-menu ${isMenuOpen ? 'active' : ''}`} id="nav-menu" aria-label="모바일 내비게이션">
            {/* 홈 */}
            <li className="nav-item">
              <a
                href="/"
                className={isMobile() ? 'mobile-row' : ''}
                onClick={() => { if (isMenuOpen) toggleMenu(); }}
              >
                <span className="mobile-label">홈</span>
                <span className="chev arrow-right" aria-hidden="true" />
              </a>
            </li>

            {data.menu?.map((m, idx) => {
              const children = getChildren(m);
              const key = m.href || m.label || `m-${idx}`;
              const isOpen = openSet.has(key);

              if (children) {
                return (
                  <li
                    key={key}
                    className="nav-item has-dropdown"
                    onMouseEnter={() => { if (!isMobile()) setOpenSet(new Set([key])); }}
                    onMouseLeave={() => { if (!isMobile()) setOpenSet(new Set()); }}
                  >
                    {isMobile() ? (
                      <button
                        type="button"
                        className="mobile-row mobile-section-btn"
                        aria-expanded={isOpen}
                        onClick={() => toggleSection(key)}
                      >
                        <span className="mobile-label">{m.label}</span>
                        <span className={`chev ${isOpen ? 'chev-up' : 'chev-down'}`} aria-hidden="true" />
                      </button>
                    ) : (
                      <a href={m.href || '#'} className="desktop-parent">
                        {m.label}
                      </a>
                    )}

                    <ul className={`dropdown-menu ${isMobile() ? 'mobile-accordion' : ''} ${isOpen ? 'show' : ''}`}>
                      {children.map((sub, i) => (
                        <li key={`${key}-${i}`}>
                          <a
                            href={sub.href}
                            className={isMobile() ? 'mobile-subrow' : ''}
                            onClick={() => { if (isMenuOpen) toggleMenu(); }}
                          >
                            <span className="mobile-sublabel">{sub.label}</span>
                            {isMobile() && <span className="chev arrow-right" aria-hidden="true" />}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              }

              return (
                <li key={key} className="nav-item">
                  <a
                    href={m.href || '#'}
                    className={isMobile() ? 'mobile-row' : ''}
                    onClick={(e) => {
                      if (m.href?.startsWith('#')) onAnchorClick(e);
                      else if (isMenuOpen) toggleMenu();
                    }}
                  >
                    <span className="mobile-label">{m.label}</span>
                    {isMobile() && <span className="chev arrow-right" aria-hidden="true" />}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <button
          type="button"
          className={`mobile-menu-toggle ${isMenuOpen ? 'opened' : ''}`}
          id="mobile-toggle"
          onClick={toggleMenu}
          aria-expanded={isMenuOpen}
          aria-label="메뉴 열기/닫기"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </header>
  );
}